/**
 * KAN-826: option "Дополнить уже загруженных" of the list upload form (not
 * remembered in presets) with its preview and the background job panel:
 * progress bar while the job runs, then counters, merges of organizations
 * ("X -> Y, контактов N") and skipped merges. A preview writes nothing; from
 * its panel the real run can be started.
 * KAN-861: renamed to "Обновить данные у тех, кто уже в базе (без новой
 * рассылки)" and moved into the collapsed section "Дополнительно" (closed by
 * default, opened while the option is on or a job panel is shown).
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Eye, Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { UploadUpdateJobView } from '@/hooks/useUploadUpdateJob';

interface UploadUpdateExistingProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  job: UploadUpdateJobView | null;
  busy: boolean;
  onPreview: () => void;
  onRun: () => void;
}

function MergeList({ testId, title, items }: { testId: string; title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div data-testid={testId}>
      <p className="font-medium mb-1">{title}</p>
      <ul className="list-disc pl-5 space-y-0.5">
        {items.map((item, index) => (
          <li key={`${index}-${item}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function JobPanel({ job, busy, onRun }: { job: UploadUpdateJobView; busy: boolean; onRun: () => void }) {
  const preview = job.mode === 'preview';
  const running = job.status === 'pending' || job.status === 'running';
  const title = running
    ? preview
      ? 'Готовим предпросмотр...'
      : 'Дополняем загруженных...'
    : job.status === 'failed'
      ? 'Не получилось'
      : preview
        ? 'Предпросмотр: ничего не записано'
        : 'Дополнение завершено';

  return (
    <div data-testid="upload-job-panel" className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3 text-sm">
      <p data-testid="upload-job-title" className="font-medium text-gray-900">
        {title}
      </p>
      {job.importSummary && (
        <p data-testid="upload-job-import" className="text-gray-700">
          Новые строки: {job.importSummary}
        </p>
      )}
      {running && (
        <div className="space-y-1">
          <div
            role="progressbar"
            aria-label="Ход дополнения"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={job.percent ?? undefined}
            data-testid="upload-job-progress"
            className="h-2 w-full overflow-hidden rounded bg-gray-200"
          >
            <div
              className={job.percent === null ? 'h-full w-1/3 animate-pulse bg-blue-500' : 'h-full bg-blue-500'}
              style={job.percent === null ? undefined : { width: `${job.percent}%` }}
            />
          </div>
          {job.progressText && <p className="text-xs text-gray-500">{job.progressText}</p>}
        </div>
      )}
      {job.error && (
        <p data-testid="upload-job-error" className="text-red-700">
          {job.error}
        </p>
      )}
      {job.status === 'done' && (
        <>
          <ul data-testid="upload-job-counters" className="space-y-1">
            {job.counters.map((counter) => (
              <li key={counter.key}>
                {counter.label}: <strong>{counter.value}</strong>
              </li>
            ))}
          </ul>
          <MergeList testId="upload-job-merged" title="Объединение организаций" items={job.merged} />
          <MergeList testId="upload-job-skipped" title="Пропущенные объединения" items={job.skipped} />
          {preview && (
            <Button
              type="button"
              data-testid="upload-job-run"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={onRun}
              disabled={busy}
            >
              <Play className="h-4 w-4 mr-2" />
              Дополнить
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export function UploadUpdateExisting({
  checked,
  onChange,
  job,
  busy,
  onPreview,
  onRun,
}: UploadUpdateExistingProps): JSX.Element {
  const [open, setOpen] = useState(checked || job !== null);
  const mustShow = checked || job !== null;
  useEffect(() => {
    if (mustShow) setOpen(true);
  }, [mustShow]);

  return (
    <div data-testid="upload-advanced" className="bg-white border rounded-lg">
      <button
        type="button"
        data-testid="upload-advanced-toggle"
        aria-expanded={open}
        aria-controls="upload-advanced-body"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 p-4 text-sm font-medium text-gray-700"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Дополнительно
      </button>
      {open && (
        <div id="upload-advanced-body" className="px-4 pb-4 space-y-3">
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <Checkbox data-testid="upload-update-existing" checked={checked} onCheckedChange={onChange} />
              Обновить данные у тех, кто уже в базе (без новой рассылки)
            </label>
            <p data-testid="upload-update-existing-hint" className="text-xs text-gray-500">
              Если в таблице появились новые столбцы: допишет пустые реквизиты организаций и свои
              поля клиникам, которые уже загружены. Новых строк в рассылку не добавит, ничего не
              перезапишет.
            </p>
          </div>
          {checked && (
            <Button type="button" variant="outline" data-testid="upload-job-preview" onClick={onPreview} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Предпросмотр
            </Button>
          )}
          {job && <JobPanel job={job} busy={busy} onRun={onRun} />}
        </div>
      )}
    </div>
  );
}
